// Audio helpers — Web Audio API chimes and rings. Single shared context so we
// don't leak oscillators across the page lifetime.

let _sharedAudioCtx = null;

export function getAudioCtx() {
    if (typeof window === "undefined") return null;
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    if (!_sharedAudioCtx) _sharedAudioCtx = new AC();
    if (_sharedAudioCtx.state === "suspended") {
        _sharedAudioCtx.resume().catch(() => {});
    }
    return _sharedAudioCtx;
}

// Soft two-note completion chime
export function playChime() {
    const ctx = getAudioCtx();
    if (!ctx) return;
    const now = ctx.currentTime;
    const notes = [
        { f: 880,    t: now,        d: 0.18 },  // A5
        { f: 1318.5, t: now + 0.18, d: 0.32 },  // E6
    ];
    notes.forEach(({ f, t, d }) => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = "sine";
        o.frequency.value = f;
        g.gain.setValueAtTime(0.0001, t);
        g.gain.exponentialRampToValueAtTime(0.22, t + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, t + d);
        o.connect(g).connect(ctx.destination);
        o.start(t);
        o.stop(t + d + 0.05);
    });
}

// Soft "squish" sound — short organic downward chirp + low resonant body.
// Designed to feel like pressing a squishy object: a quick high-to-low
// frequency sweep + a soft thump underneath, no harshness.
export function playSquish() {
    const ctx = getAudioCtx();
    if (!ctx) return;
    const now = ctx.currentTime;

    // 1) Downward "squeeze" chirp — triangle wave swept 520 Hz → 180 Hz
    const chirp = ctx.createOscillator();
    const chirpGain = ctx.createGain();
    chirp.type = "triangle";
    chirp.frequency.setValueAtTime(520, now);
    chirp.frequency.exponentialRampToValueAtTime(180, now + 0.18);
    chirpGain.gain.setValueAtTime(0.0001, now);
    chirpGain.gain.exponentialRampToValueAtTime(0.13, now + 0.015);
    chirpGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.22);
    chirp.connect(chirpGain).connect(ctx.destination);
    chirp.start(now);
    chirp.stop(now + 0.25);

    // 2) Low-pass-ish body thump — sine 90 Hz
    const body = ctx.createOscillator();
    const bodyGain = ctx.createGain();
    body.type = "sine";
    body.frequency.setValueAtTime(120, now);
    body.frequency.exponentialRampToValueAtTime(70, now + 0.16);
    bodyGain.gain.setValueAtTime(0.0001, now);
    bodyGain.gain.exponentialRampToValueAtTime(0.18, now + 0.02);
    bodyGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.22);
    body.connect(bodyGain).connect(ctx.destination);
    body.start(now);
    body.stop(now + 0.25);
}

export function playRing(repeats = 3) {
    const ctx = getAudioCtx();
    if (!ctx) return;
    // Bell-like reminder ring with harmonics, "ding-dong" pattern, repeats N times.
    const ringAt = (startOffset) => {
        const start = ctx.currentTime + startOffset;
        [
            { f: 880,    t: start,         d: 1.4 },
            { f: 659.25, t: start + 0.42,  d: 1.6 },
        ].forEach(({ f, t, d }) => {
            [
                { mul: 1,    gain: 0.45 },
                { mul: 2.01, gain: 0.20 },
                { mul: 3.02, gain: 0.10 },
                { mul: 4.05, gain: 0.05 },
            ].forEach(({ mul, gain }) => {
                const o = ctx.createOscillator();
                const g = ctx.createGain();
                o.type = "sine";
                o.frequency.value = f * mul;
                g.gain.setValueAtTime(0.0001, t);
                g.gain.exponentialRampToValueAtTime(gain, t + 0.01);
                g.gain.exponentialRampToValueAtTime(0.0001, t + d);
                o.connect(g).connect(ctx.destination);
                o.start(t);
                o.stop(t + d + 0.05);
            });
        });
    };
    for (let i = 0; i < repeats; i++) ringAt(i * 1.1);
}


// Soft two-note cover-pass chime — lighter, more playful than playChime().
// Used when Mochi uses her weekly streak-cover pass on a habit.
// G5 → B5 (a major third up, conspiratorial wink-y feel).
export function playCoverChime() {
    const ctx = getAudioCtx();
    if (!ctx) return;
    const now = ctx.currentTime;
    const notes = [
        { f: 783.99, t: now,         d: 0.16 },  // G5
        { f: 987.77, t: now + 0.14,  d: 0.28 },  // B5
    ];
    notes.forEach(({ f, t, d }) => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = "triangle"; // softer/playful vs the sine of playChime
        o.frequency.value = f;
        g.gain.setValueAtTime(0.0001, t);
        g.gain.exponentialRampToValueAtTime(0.16, t + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, t + d);
        o.connect(g).connect(ctx.destination);
        o.start(t);
        o.stop(t + d + 0.05);
    });
}
